import React, { useState, useEffect } from 'react';
import { Box, Stepper, Step, StepLabel, Typography, Button, Stack, Divider, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import CancelIcon from '@mui/icons-material/Cancel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import AddIcon from '@mui/icons-material/Add';
import Paper from '@mui/material/Paper';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid'; // 用于生成唯一文件名
// 需要安装：npm install uuid
import { QRCode } from 'react-qrcode-logo'; // 需要安装：npm install react-qrcode-logo

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const defaultOrderData = {
  payOrderId: '',
  productId: 0,
  payAmount: 0,
  name: '',
  cardno: '',
  bank: '',
  branch: '',
};

const steps = ['确认付款', '等待确认', '确认收款'];
const stepDesc = [
  '请向收款人付款，并上传付款凭证。',
  '等待确认收款，请耐心等待。',
  '收款确认后，订单完成。',
];

const Order: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0); // 0:确认付款, 1:等待确认, 2:确认收款
  const [orderData, setOrderData] = useState(defaultOrderData);
  const [loading, setLoading] = useState(false);
  const query = useQuery();
  const payOrderId = query.get('orderId');
  const [showPayTip, setShowPayTip] = useState(true);
  const [snackbar, setSnackbar] = useState<{open: boolean, msg: string}>({open: false, msg: ''});
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  // 批量上传文件到本地服务器的函数
  const uploadFilesToServer = async (files: File[]) => {
    const formData = new FormData();
    formData.append('payOrderId', orderData.payOrderId);
    
    // 添加所有文件，参数名为 'files'
    files.forEach((file) => {
      formData.append('files', file);
    });
    
    const response = await axios.post(
      'https://api.seahorse.quest/api/s3/order_uploadfile',
      formData,
      { 
        headers: { 
          'Content-Type': 'multipart/form-data'
        } 
      }
    );
    
    return response.data; // 返回文件上传后的信息
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({open: true, msg: '已复制'});
  };
  const handleUploadClose = () => { setUploadOpen(false); setUploadFiles([]); setPreviewUrls([]); };
  const handleUploadClick = () => setUploadOpen(true);
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    let validFiles = [...uploadFiles];
    let validUrls = [...previewUrls];
    for (let file of files) {
      if (validFiles.length >= 3) break;
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!['jpg','jpeg','png'].includes(ext||'')) {
        setSnackbar({open:true, msg:'仅支持jpg/png'}); continue;
      }
      if (file.size > 3*1024*1024) {
        setSnackbar({open:true, msg:'单文件不超过3M'}); continue;
      }
      validFiles.push(file);
      validUrls.push(URL.createObjectURL(file));
    }
    if (validFiles.length > 3) {
      validFiles = validFiles.slice(0,3);
      validUrls = validUrls.slice(0,3);
    }
    setUploadFiles(validFiles);
    setPreviewUrls(validUrls);
    e.target.value = '';
  };
  const handleRemoveFile = (idx: number) => {
    setUploadFiles(files => files.filter((_,i)=>i!==idx));
    setPreviewUrls(urls => urls.filter((_,i)=>i!==idx));
  };
  // 在 handleUploadSubmit 里批量上传
  const handleUploadSubmit = async () => {
    if (uploadFiles.length === 0) {
      setSnackbar({open:true, msg:'请上传图片'}); return;
    }
    setLoading(true);
    try {
      // 第一步：一次性上传所有文件到服务器
      const result = await uploadFilesToServer(uploadFiles);
      
      if (result.retCode === 'success') {
        // 从返回结果中提取文件名
        const uploadedFiles = result.files ? result.files.map((fileInfo: any) => fileInfo.fileName) : [];
        
        // 第二步：提交订单信息到commit_order接口
        const paramsObj: Record<string, any> = {
          payOrderId: orderData.payOrderId,
          OfflineName: orderData.name,
          OfflineCardNo: orderData.cardno,
        };
        
        // 添加上传成功的文件信息
        uploadedFiles.forEach((fileName: string, idx: number) => {
          paramsObj[`UploadFile${idx+1}`] = fileName;
        });
        
        await axios.post(
          'https://api.seahorse.quest/api/pay/commit_order',
          new URLSearchParams({ params: JSON.stringify(paramsObj) }).toString(),
          { headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' } }
        ).then(res => {
          let data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
          if (data.retCode === 'success') {
            setSnackbar({open:true, msg:'提交成功，等待确认'});
            setActiveStep(1); // 进入下一步
          } else {
            setSnackbar({open:true, msg:data.retMsg||'提交失败'});
          }
        }).catch(err => {
          setSnackbar({open:true, msg:'提交失败'});
        });
        setUploadOpen(false);
        setUploadFiles([]);
        setPreviewUrls([]);
      } else {
        throw new Error(result.retMsg || '文件上传失败');
      }
    } catch (e) {
      setSnackbar({open:true, msg:'上传失败'});
    }
    setLoading(false);
  };
  // 倒计时相关
  const [countdown, setCountdown] = useState(600); // 秒
  const [cancelled, setCancelled] = useState(false);
  useEffect(() => {
    if (cancelled) return;
    if (countdown <= 0) {
      setCancelled(true);
      return;
    }
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, cancelled]);

  useEffect(() => {
    if (!payOrderId) return;
    setLoading(true);
    axios.post(
      'https://api.seahorse.quest/api/pay/open_order',
      new URLSearchParams({ params: payOrderId || '' }).toString(),
      { headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' } }
    )
      .then(res => {
        let data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        if (data.retCode === 'success') {
          setOrderData(data);
        } else {
          setSnackbar({open:true, msg:data.retMsg||'订单获取失败'});
        }
      })
      .catch(err => {
        setSnackbar({open:true, msg:'网络错误'});
      })
      .finally(()=>setLoading(false));
  }, [payOrderId]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // PC大屏下分栏样式
  const infoGrid = isMobile ? {
    display: 'block',
  } : {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    alignItems: 'start',
  };
  const infoLabel = { color: '#888', fontSize: isMobile ? 13 : 15, marginBottom: 2 };
  const infoValue = { color: '#222', fontWeight: 600, fontSize: isMobile ? 16 : 20, wordBreak: 'break-all' };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', my: 4, p: isMobile ? 1 : 0 }}>
      {/* 步骤栏或取消提示 */}
      {!cancelled ? (
        <Box sx={{ background: '#f5f8ff', borderRadius: 2, px: isMobile ? 1 : 3, py: 2, mb: 3, position: 'relative' }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 1, '& .MuiStepLabel-root': { fontWeight: 600 } }}>
            {steps.map((label, idx) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Typography align="center" color="#1976d2" fontSize={isMobile ? 13 : 15}>{stepDesc[activeStep]}</Typography>
          {/* 倒计时 */}
          <Box sx={{ position: 'absolute', right: 24, top: 16, fontSize: isMobile ? 15 : 18, color: '#d32f2f', fontWeight: 700 }}>
            {Math.floor(countdown/60)}:{(countdown%60).toString().padStart(2,'0')}
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 6, mb: 3 }}>
          <CancelIcon sx={{ fontSize: 64, color: '#d32f2f', mb: 2 }} />
          <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#d32f2f' }}>交易已取消</Typography>
        </Box>
      )}

      {/* 订单号 */}
      <Box sx={{ background: '#1976d2', color: '#fff', borderRadius: 1, p: 1.5, mb: 2, textAlign: 'center', fontWeight: 600, fontSize: isMobile ? 15 : 18, letterSpacing: 1 }}>
        订单号：{orderData.payOrderId}
      </Box>

      {/* 金额行 */}
      <Box sx={{ mb: 2, textAlign: 'center' }}>
        <Typography fontSize={isMobile ? 15 : 18} color="#888">请付款</Typography>
        <Typography fontSize={isMobile ? 28 : 38} color="#1976d2" fontWeight={700} lineHeight={1.2}>
          {orderData.payAmount ? (orderData.payAmount / 100) : ''} CNY
        </Typography>
      </Box>

      {/* 分栏标题、内容、提示、按钮，仅未取消时显示 */}
      {!cancelled && <>
        {/* 分栏标题 */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {/* productId判断 */}
          {(orderData.productId === 8006 || orderData.productId === 8005) ? (
            <span style={{display:'flex',alignItems:'center'}}>
              {/* 支付宝/微信SVG图标 */}
              {orderData.productId === 8006 ? (
                <img src={process.env.PUBLIC_URL + '/zfb.png'} alt="支付宝" style={{width:28,height:28,marginRight:8,verticalAlign:'middle'}} />
              ) : (
                <img src={process.env.PUBLIC_URL + '/weixin.png'} alt="微信" style={{width:28,height:28,marginRight:8,verticalAlign:'middle'}} />
              )}
              <Typography fontWeight={700} fontSize={isMobile ? 16 : 18}>{orderData.productId === 8006 ? '支付宝收款' : '微信收款'}</Typography>
            </span>
          ) : (
            <>
              <CreditCardIcon color="primary" sx={{ mr: 1 }} />
              <Typography fontWeight={700} fontSize={isMobile ? 16 : 18}>银行卡收款</Typography>
            </>
          )}
        </Box>

        {/* 分栏内容 */}
        <Box sx={{ ...infoGrid, background: '#f7f8fa', borderRadius: 2, p: 2, mb: 2 }}>
          {/* 支付宝模式 */}
          {(orderData.productId === 8006 || orderData.productId === 8005) ? (
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 2 : 6 }}>
              {/* 左侧：卖家姓名、支付宝账号 */}
              <Box sx={{ flex: isMobile ? 'unset' : '0 0 340px', maxWidth: 380, minWidth: isMobile ? 'unset' : 220, pr: isMobile ? 0 : 2 }}>
                {/* 卖家姓名 */}
                <Typography sx={infoLabel}>卖家姓名</Typography>
                <Box sx={{ display:'flex', alignItems:'center', mb:2 }}>
                  <Typography sx={infoValue} component="span">{orderData.name}</Typography>
                  <IconButton size="small" sx={{ ml: 1, p: '4px' }} onClick={()=>handleCopy(orderData.name)}>
                    <ContentCopyIcon fontSize="small" sx={{ color: '#1976d2' }} />
                  </IconButton>
                </Box>
                {/* 支付宝账号 */}
                <Typography sx={infoLabel}>账号</Typography>
                <Box sx={{ display:'flex', alignItems:'center', mb:2 }}>
                  <Typography sx={infoValue} component="span">{orderData.bank}</Typography>
                  <IconButton size="small" sx={{ ml: 1, p: '4px' }} onClick={()=>handleCopy(orderData.bank)}>
                    <ContentCopyIcon fontSize="small" sx={{ color: '#1976d2' }} />
                  </IconButton>
                </Box>
              </Box>
              {/* 右侧：扫码转账二维码 */}
              <Box sx={{ flex: isMobile ? 'unset' : '1', display:'flex', flexDirection:'column', alignItems: isMobile ? 'center' : 'flex-end', justifyContent:'center', minWidth: isMobile ? 'unset' : 220 }}>
                <Typography sx={infoLabel}>扫码转账</Typography>
                <Box sx={{mt:1, display:'flex', justifyContent: isMobile ? 'center' : 'flex-end', alignItems:'center', width: isMobile ? '100%' : 180 }}>
                  <img src={orderData.cardno} alt="扫码转账" style={{width: isMobile ? 240 : 260, height: isMobile ? 280 : 360, objectFit: 'contain', borderRadius: 8, background: '#fafbfc'}} />
                </Box>
              </Box>
            </Box>
          ) : (
            <>
              {/* 卖家姓名 */}
              <Box sx={{ mb: isMobile ? 2 : 0, display: 'flex', alignItems: 'center' }}>
                <Box>
                  <Typography sx={infoLabel}>卖家姓名</Typography>
                  <Typography sx={infoValue} component="span">{orderData.name}</Typography>
                </Box>
                <IconButton size="small" sx={{ ml: 1, p: '4px' }} onClick={()=>handleCopy(orderData.name)}>
                  <ContentCopyIcon fontSize="small" sx={{ color: '#1976d2' }} />
                </IconButton>
              </Box>
              {/* 账号 */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box>
                  <Typography sx={infoLabel}>账号</Typography>
                  <Typography sx={infoValue} component="span">{orderData.cardno}</Typography>
                </Box>
                <IconButton size="small" sx={{ ml: 1, p: '4px' }} onClick={()=>handleCopy(orderData.cardno)}>
                  <ContentCopyIcon fontSize="small" sx={{ color: '#1976d2' }} />
                </IconButton>
              </Box>
              {/* 开户银行 */}
              <Box sx={{ mb: isMobile ? 2 : 0, display: 'flex', alignItems: 'center' }}>
                <Box>
                  <Typography sx={infoLabel}>开户银行</Typography>
                  <Typography sx={infoValue} component="span">{orderData.bank}</Typography>
                </Box>
                <IconButton size="small" sx={{ ml: 1, p: '4px' }} onClick={()=>handleCopy(orderData.bank)}>
                  <ContentCopyIcon fontSize="small" sx={{ color: '#1976d2' }} />
                </IconButton>
              </Box>
              {/* 支行信息 */}
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box>
                  <Typography sx={infoLabel}>支行信息</Typography>
                  <Typography sx={infoValue} component="span">{orderData.branch}</Typography>
                </Box>
                <IconButton size="small" sx={{ ml: 1, p: '4px' }} onClick={()=>handleCopy(orderData.branch)}>
                  <ContentCopyIcon fontSize="small" sx={{ color: '#1976d2' }} />
                </IconButton>
              </Box>
            </>
          )}
        </Box>

        {/* 提示信息 */}
        <Box sx={{ background: '#fffbe6', borderRadius: 1, p: 2, mb: 2, color: '#ad8b00', fontSize: isMobile ? 13 : 15 }}>
          <div>· 请务必本人支付，勿找人代付</div>
          <div>
            <span style={{ color: '#d32f2f', fontWeight: 600 }}>· 付款后请务必点击我已完成付款，</span>若未点击将导致到账延时
          </div>
          {/* 支付宝专属提示 */}
          {orderData.productId === 8006 && (
            <div>
              · 如有疑问，请直接联系平台客服处理，<span style={{color:'#d32f2f',fontWeight:600}}>请勿与支付宝平台客服沟通</span>
            </div>
          )}
        </Box>

        {/* 步骤按钮 */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 6 }}>
          <Button
            variant="outlined"
            onClick={() => setCancelled(true)}
            sx={{ minWidth: { xs: 140, sm: 200 }, fontSize: { xs: 16, sm: 20 }, py: 1.2 }}
          >
            取消订单
          </Button>
          <Box id="pay-done-btn-wrap" sx={{ display: 'inline-block', position: 'relative' }}>
            <Button
              id="pay-done-btn"
              variant="contained"
              disabled={activeStep >= 2}
              onClick={handleUploadClick}
              sx={{ minWidth: { xs: 140, sm: 200 }, fontSize: { xs: 16, sm: 20 }, py: 1.2 }}
            >
              我已付款完成
            </Button>
            {/* 提示信息栏，按钮下方，左对齐并带箭头在上方 */}
            {showPayTip && activeStep === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', position: 'absolute', left: 0, top: '100%', mt: 1, zIndex: 10, pointerEvents: 'auto' }}>
                {/* 箭头，上方左对齐 */}
                <Box sx={{
                  width: 0, height: 0,
                  borderLeft: '12px solid transparent',
                  borderRight: '12px solid transparent',
                  borderBottom: '12px solid #222',
                  alignSelf: 'flex-start',
                  ml: 8,
                  mb: '-2px',
                }} />
                <Alert
                  sx={{
                    mt: 0,
                    mb: 0,
                    width: { xs: 250, sm: 320 },
                    fontSize: { xs: 13, sm: 14 },
                    alignItems: 'center',
                    borderRadius: 2,
                    background: '#222',
                    color: '#fff',
                    boxShadow: 3,
                    '.MuiAlert-message': { width: '100%', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }
                  }}
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => setShowPayTip(false)}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                  icon={false}
                >
                  <span style={{fontWeight: 500}}>若您已完成付款，请务必点击
                    <span style={{color:'#ff3b3b', fontWeight:700, marginLeft:4}}>我已付款完成</span>
                  </span>
                </Alert>
              </Box>
            )}
          </Box>
        </Box>
      </>}

      {/* 分割线 */}
      <Divider sx={{ my: 2 }} />

      {/* 页脚提示语 */}
      <Box sx={{ color: '#888', fontSize: isMobile ? 12 : 14, lineHeight: 1.8 }}>
        <div>
          1. 每笔订单会生成唯一的收款信息，请不要保存信息进行二次转账，
          <span style={{ color: '#d32f2f', fontWeight: 600 }}>转账时请不要添加任何备注信息</span>。
        </div>
        <div>2. 请您在规定时间内完成汇款后务必点击“我已完成汇款”按钮，以免系统将取消订单。</div>
        <div>3. 您的汇款将直接转入卖家账户，可放心交易。</div>
        <div>4. 本平台暂不支持少数民族同胞的入金交易，敬请谅解。</div>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={1500}
        onClose={()=>setSnackbar({open:false, msg:''})}
        message={snackbar.msg}
        anchorOrigin={{vertical:'top', horizontal:'center'}}
      />
      {/* 上传凭证对话框 */}
      <Dialog open={uploadOpen} onClose={handleUploadClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', pr:1 }}>
          上传付款凭证
          <IconButton onClick={handleUploadClose}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>请上传支付凭证截图</Typography>
          <Typography sx={{ mb: 2, color:'#d32f2f', fontSize:13, fontWeight: 600 }}>请确保支付凭证信息完整</Typography>
          <Paper variant="outlined" sx={{ border:'2px dashed #bbb', borderRadius:2, p:2, mb:2, textAlign:'center', cursor:'pointer', minHeight:160, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', position:'relative' }} onClick={()=>uploadFiles.length<3 && document.getElementById('file-upload-input')?.click()}>
            <input id="file-upload-input" type="file" multiple accept=".jpg,.jpeg,.png" style={{display:'none'}} onChange={handleFileChange} />
            {previewUrls.length === 0 ? (
              <>
                <AddIcon sx={{ fontSize:40, color:'#1976d2', mb:1 }} />
                <Typography color="#888" fontSize={14}>点击选择上传图片</Typography>
              </>
            ) : (
              <Box sx={{ display:'flex', gap:2 }}>
                {previewUrls.map((url, idx) => (
                  <Box key={idx} sx={{ position:'relative', width:100, height:100, display:'flex', alignItems:'center', justifyContent:'center', background:'#fafbfc', borderRadius:2 }}>
                    <img src={url} alt="预览" style={{ width:'100%', height:'100%', objectFit:'contain', borderRadius:8, background:'#fafbfc' }} />
                    <IconButton size="small" sx={{ position:'absolute', top:4, right:4, bgcolor:'#fff' }} onClick={e=>{e.stopPropagation();handleRemoveFile(idx);}}><CloseIcon fontSize="small" /></IconButton>
                  </Box>
                ))}
                {uploadFiles.length<3 && (
                  <Box sx={{ width:100, height:100, display:'flex', alignItems:'center', justifyContent:'center', border:'1px dashed #bbb', borderRadius:2, cursor:'pointer', bgcolor:'#fafbfc' }}>
                    <AddIcon sx={{ fontSize:32, color:'#1976d2' }} />
                  </Box>
                )}
              </Box>
            )}
          </Paper>
          <Typography sx={{ color:'#888', fontSize:13, mb:0.5 }}>• 目前只支持jpg、png格式</Typography>
          <Typography sx={{ color:'#888', fontSize:13, mb:0.5 }}>• 文件大小不超过3M</Typography>
          <Typography sx={{ color:'#888', fontSize:13, mb:2 }}>• 最多可上传3个文件</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" fullWidth onClick={handleUploadSubmit}>确认提交</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Order; 